// ─────────────────────────────────────────────────────────────────────────────
// spell_registry.ts — Spell templates, dispatch, and the Codex
//
// A SpellTemplate defines a composable unit of notebook behaviour.
// Tier 0 spells map 1:1 to GrimoireActions (atomic).
// Tier 1+ spells are compositions of lower-tier SpellTemplates.
//
// The dispatch table is the API — agents, gestures, and keyboard all call
// SpellRegistry.cast() with a spellId and parameters.
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction, SpellFamily, SpellDispatchFn, ActionOrigin } from '../shared/grimoire_action.js';
import type { BoundingBox, RegionBounds, BlockId, PageId, StrokeId } from '../shared/notebook_model.js';
import type { InitiatorFamily } from './gesture_recognizer.js';

// ── Spell parameter descriptors ───────────────────────────────────────────────

export type SpellParamSource =
  | 'stroke_length'   // from gesture geometry
  | 'lasso_bounds'    // region drawn by the user
  | 'point_target'    // single point (block under pen tip)
  | 'drag_vector'     // displacement from drag gesture
  | 'current_page'    // resolved by the registry from notebook state
  | 'explicit';       // provided directly by caller (agent/keyboard)

export interface SpellParamDescriptor {
  name: string;
  source: SpellParamSource;
  required: boolean;
  description: string;
}

// ── Charge function types ────────────────────────────────────────────────────
// charge_ms = chargeFn(stroke_length_px).  Larger strokes → more time to compose.

export type ChargeFn = (strokeLengthPx: number) => number;

export const LINEAR_CHARGE: ChargeFn = (len) => Math.min(Math.max(len * 2, 150), 2000);
export const INSTANT_CHARGE: ChargeFn = (_len) => 180;

// ── Spell branch — sub-spell executed when wavefront reaches its anchor ───────

export interface SpellBranch {
  /** 0–1 position along the charge timeline when this branch fires. */
  anchorT: number;
  subSpellId: string;
  /** How parameters are inherited or overridden from the parent. */
  paramMap: Record<string, string | unknown>;
}

// ── Tier levels ───────────────────────────────────────────────────────────────

export type SpellTier = 0 | 1 | 2 | 3;
// 0 — Primitive: single GrimoireAction per cast
// 1 — Common: known to the student from the start, may have branches
// 2 — Compound: hinted by higher spells, player must discover by composition
// 3 — Arcane/Legendary: hidden until Tier 1+2 prerequisites are met

// ── SpellTemplate ─────────────────────────────────────────────────────────────

export interface SpellTemplate {
  id: string;
  tier: SpellTier;
  name: string;
  lore: string;                 // in-world description shown in the Codex
  family: SpellFamily;
  discovered: boolean;          // false = exists in registry but hidden from player
  params: SpellParamDescriptor[];
  chargeFn: ChargeFn;
  branches: SpellBranch[];      // sub-spells fired during charge
  /** Returns the GrimoireAction(s) to emit when this spell resolves. */
  resolve(params: Record<string, unknown>, origin: ActionOrigin): GrimoireAction[];
}

// ── SpellRegistry ─────────────────────────────────────────────────────────────

export class SpellRegistry {
  private readonly _spells = new Map<string, SpellTemplate>();
  private _dispatch: SpellDispatchFn | null = null;

  setDispatch(fn: SpellDispatchFn): void {
    this._dispatch = fn;
  }

  register(spell: SpellTemplate): void {
    this._spells.set(spell.id, spell);
  }

  get(id: string): SpellTemplate | undefined {
    return this._spells.get(id);
  }

  /**
   * Cast a spell by ID.  Parameters are merged from the template descriptor
   * and the provided values.  Actions are dispatched immediately if no charge,
   * or handed to the ChargeEngine if chargeDurationMs > 0.
   */
  cast(
    spellId: string,
    params: Record<string, unknown>,
    origin: ActionOrigin,
  ): GrimoireAction[] | null {
    const spell = this._spells.get(spellId);
    if (!spell) {
      console.warn(`[SpellRegistry] Unknown spell: ${spellId}`);
      return null;
    }

    const actions = spell.resolve(params, origin);
    if (this._dispatch) {
      for (const a of actions) { this._dispatch(a); }
    }
    return actions;
  }

  /** Discovered spells only — for the player-facing Codex. */
  codex(): SpellTemplate[] {
    return [...this._spells.values()].filter(s => s.discovered);
  }

  /** All spells including undiscovered — for internal use. */
  all(): SpellTemplate[] {
    return [...this._spells.values()];
  }

  discover(spellId: string): void {
    const spell = this._spells.get(spellId);
    if (spell && !spell.discovered) {
      spell.discovered = true;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Built-in Tier 0 / Tier 1 spell templates
// ─────────────────────────────────────────────────────────────────────────────

/** Factory for the standard spell set that ships with the Grimoire. */
export function registerBuiltinSpells(registry: SpellRegistry): void {

  // ── Tier 1: erase.region ─────────────────────────────────────────────────────
  registry.register({
    id: 'erase.region',
    tier: 1,
    name: 'Erase Region',
    lore: 'Draw a lasso around a region, then slash through it to erase all ink and blocks inside.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'region', source: 'lasso_bounds', required: true, description: 'Lasso region to erase' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) {
      return [{
        type: 'ink.erase',
        pageId: params['pageId'] as PageId,
        target: params['region'] as RegionBounds,
        origin,
        timestamp: Date.now(),
      }];
    },
  });

  // ── Tier 1: select.point ────────────────────────────────────────────────────
  registry.register({
    id: 'select.point',
    tier: 1,
    name: 'Point Select',
    lore: 'Short tap from the knot downward onto a block to select it.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'target', source: 'point_target', required: true, description: 'Block under pen tip at release' },
    ],
    chargeFn: LINEAR_CHARGE,
    branches: [],
    resolve(params, origin) {
      return [{
        type: 'block.create', // select.point resolves to a synthetic block.create action
        blockId: params['target'] as BlockId,
        pageId: params['pageId'] as PageId,
        kind: 'ink',
        bbox: { x: 0, y: 0, width: 0, height: 0 },
        origin,
        timestamp: Date.now(),
      }];
    },
  });



  // ── Tier 1: erase.line ──────────────────────────────────────────────────────
  registry.register({
    id: 'erase.line',
    tier: 1,
    name: 'Line Erase',
    lore: 'Draw a straight line through strokes to erase everything it crosses.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'lineStart', source: 'stroke_length', required: true, description: 'Start point' },
      { name: 'lineEnd', source: 'stroke_length', required: true, description: 'End point' },
      { name: 'lineBbox', source: 'lasso_bounds', required: true, description: 'Bounding box of the line' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) {
      return [{
        type: 'ink.erase',
        pageId: params['pageId'] as PageId,
        target: params['lineBbox'] as RegionBounds,
        origin,
        timestamp: Date.now(),
      }];
    },
  });

  // ── Tier 1: wind.fly ────────────────────────────────────────────────────────
  // Registry entry exists for the spell-name toast.  The actual cast logic
  // lives in CanvasApp._castWindFly (animation + dispatch); this registry
  // record only carries the user-facing label.
  registry.register({
    id: 'wind.fly',
    tier: 1,
    name: 'Wind',
    lore: 'A horizontal sweep with a curling tail. Blows isolated tiny strokes off the page like leaves.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve() {
      // Side-effect handled directly by CanvasApp._castWindFly; nothing to
      // dispatch from the registry path.
      return [];
    },
  });

  // arcane.open · layer.up · layer.down — RETIRED.
  // Page-layer system has been replaced by per-content modifiers
  // (lock/group/astral) and the arcane / computation pipeline was
  // dropped during the spell-engine rebuild. These registrations were
  // removed in P3 (May 2026). Any gesture template that pointed at them
  // now silently fails to resolve; the new SpellEngine owns gesture
  // dispatch when the `ox-new-spells` flag is on.

  // ── Tier 1: copy.block ─────────────────────────────────────────────────────
  registry.register({
    id: 'copy.block',
    tier: 1,
    name: 'Copy Block',
    lore: 'Draw a circle over a block to copy it to the clipboard.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'blockId', source: 'point_target', required: true, description: 'Block to copy' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) {
      return [{
        type: 'block.create',
        blockId: params['blockId'] as BlockId,
        pageId: params['pageId'] as PageId,
        kind: 'ink' as any,
        bbox: { x: 0, y: 0, width: 0, height: 0 },
        origin,
        timestamp: Date.now(),
      }];
    },
  });

  // ── Tier 1: paste.here ─────────────────────────────────────────────────────
  registry.register({
    id: 'paste.here',
    tier: 1,
    name: 'Paste Here',
    lore: 'Draw an X mark at an empty spot to paste the clipboard contents there.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'targetX', source: 'explicit', required: true, description: 'X position' },
      { name: 'targetY', source: 'explicit', required: true, description: 'Y position' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) {
      return [{
        type: 'block.create',
        blockId: `paste.${Date.now().toString(36)}`,
        pageId: params['pageId'] as PageId,
        kind: 'ink' as any,
        bbox: { x: params['targetX'] as number, y: params['targetY'] as number, width: 0, height: 0 },
        origin,
        timestamp: Date.now(),
      }];
    },
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Tier 1 — Lasso-arcane compositions
  // ─────────────────────────────────────────────────────────────────────────

  // ── Tier 1: select.region ────────────────────────────────────────────────
  registry.register({
    id: 'select.region',
    tier: 1,
    name: 'Lasso Select',
    lore: 'Draw a closed lasso around objects to select them.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'region', source: 'lasso_bounds', required: true, description: 'Selected region' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) { return []; },
  });

  // ── Tier 1: copy.region ──────────────────────────────────────────────────
  registry.register({
    id: 'copy.region',
    tier: 1,
    name: 'Lasso Copy',
    lore: 'Draw a lasso then a check mark inside to copy the lassoed contents to the clipboard.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'region', source: 'lasso_bounds', required: true, description: 'Region to copy' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) { return []; },
  });

  // ── Tier 1: snapshot.region ──────────────────────────────────────────────
  registry.register({
    id: 'snapshot.region',
    tier: 1,
    name: 'Lasso Snapshot',
    lore: 'Draw a lasso then a rectangle inside to snapshot the region to the system clipboard as an image.',
    family: 'act',
    discovered: true,
    params: [
      { name: 'region', source: 'lasso_bounds', required: true, description: 'Region to snapshot' },
      { name: 'pageId', source: 'current_page', required: true, description: '' },
    ],
    chargeFn: INSTANT_CHARGE,
    branches: [],
    resolve(params, origin) { return []; },
  });

  // arcane.eval — RETIRED in P3 (the arcane / computation pipeline went
  // away). Its lasso+rune gesture is no longer recognized.

  // ─────────────────────────────────────────────────────────────────────────
  // Tier 2 — Compound Spells (discovered through practice)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Tier 2: derive.step ─────────────────────────────────────────────────────
  // "Select a region, then a new block is born below it with a 'derives' arrow."
  // Composition: select.region → block.create → block.link
  registry.register({
    id: 'derive.step',
    tier: 2,
    name: 'Derive Step',
    lore: 'Lasso a source block then drag downward to create a new linked block below it with a derives-from arrow.',
    family: 'act',
    discovered: false,
    params: [
      { name: 'pageId', source: 'current_page', required: true, description: '' },
      { name: 'sourceRegion', source: 'lasso_bounds', required: true, description: 'Region of the source block' },
      { name: 'sourceBlockId', source: 'point_target', required: false, description: 'Block under the lasso start' },
    ],
    chargeFn: (len) => Math.min(300 + len * 0.6, 1200),
    branches: [
      { anchorT: 0.8, subSpellId: '_derive.link', paramMap: {} },
    ],
    resolve(params, origin) {
      const pageId = params['pageId'] as PageId;
      const sourceRegion = params['sourceRegion'] as RegionBounds | null;
      const sourceBlockId = params['sourceBlockId'] as BlockId | undefined;
      const now = Date.now();
      const newBlockId = `${pageId}.ink.${now.toString(36)}`;
      const arrowId = `${pageId}.arr.${now.toString(36)}`;

      const sourceBbox = sourceRegion?.bbox ?? { x: 100, y: 100, width: 200, height: 60 };
      const newBbox: BoundingBox = {
        x: sourceBbox.x,
        y: sourceBbox.y + sourceBbox.height + 40,
        width: sourceBbox.width,
        height: 80,
      };

      const actions: GrimoireAction[] = [
        {
          type: 'block.create',
          blockId: newBlockId,
          pageId,
          kind: 'ink',
          bbox: newBbox,
          origin,
          timestamp: now,
        },
      ];

      if (sourceBlockId) {
        actions.push({
          type: 'block.link',
          arrowId,
          pageId,
          fromId: sourceBlockId,
          toId: newBlockId,
          relationType: 'derives',
          confirmed: true,
          origin,
          timestamp: now,
        });
      }

      return actions;
    },
  });

  // ── Tier 2: annotate.link ───────────────────────────────────────────────────
  // "Draw a line between two blocks to create a relation arrow."
  // Composition: detect start block → detect end block → block.link
  registry.register({
    id: 'annotate.link',
    tier: 2,
    name: 'Link Blocks',
    lore: 'Draw a straight line from one block to another to create a relation arrow between them.',
    family: 'bind',
    discovered: false,
    params: [
      { name: 'pageId', source: 'current_page', required: true, description: '' },
      { name: 'fromBlockId', source: 'point_target', required: true, description: 'Block at stroke start' },
      { name: 'toBlockId', source: 'point_target', required: true, description: 'Block at stroke end' },
      { name: 'relationType', source: 'explicit', required: false, description: 'Arrow type' },
    ],
    chargeFn: (len) => Math.min(250 + len * 0.5, 800),
    branches: [],
    resolve(params, origin) {
      const pageId = params['pageId'] as PageId;
      const fromId = params['fromBlockId'] as BlockId;
      const toId = params['toBlockId'] as BlockId;
      const relType = (params['relationType'] as import('../shared/notebook_model.js').RelationType) ?? 'references';
      const now = Date.now();
      const arrowId = `${pageId}.arr.${now.toString(36)}`;
      return [{
        type: 'block.link',
        arrowId,
        pageId,
        fromId,
        toId,
        relationType: relType,
        confirmed: true,
        origin,
        timestamp: now,
      }];
    },
  });

  // ── Tier 2: block.duplicate ─────────────────────────────────────────────────
  // "Transmute gesture over a selected block duplicates it nearby."
  registry.register({
    id: 'block.duplicate',
    tier: 2,
    name: 'Duplicate Block',
    lore: 'Figure-8 gesture over a block to duplicate it nearby. The copy is independent of the original.',
    family: 'transmute',
    discovered: false,
    params: [
      { name: 'pageId', source: 'current_page', required: true, description: '' },
      { name: 'sourceBlockId', source: 'point_target', required: true, description: 'Block to duplicate' },
      { name: 'sourceBbox', source: 'explicit', required: true, description: 'Source block bounds' },
    ],
    chargeFn: LINEAR_CHARGE,
    branches: [],
    resolve(params, origin) {
      const pageId = params['pageId'] as PageId;
      const srcBbox = params['sourceBbox'] as BoundingBox;
      const now = Date.now();
      const newBlockId = `${pageId}.ink.${now.toString(36)}`;
      return [{
        type: 'block.create',
        blockId: newBlockId,
        pageId,
        kind: 'ink',
        bbox: {
          x: srcBbox.x + 30,
          y: srcBbox.y + 30,
          width: srcBbox.width,
          height: srcBbox.height,
        },
        origin,
        timestamp: now,
      }];
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createSpellRegistry(dispatch: SpellDispatchFn): SpellRegistry {
  const registry = new SpellRegistry();
  registry.setDispatch(dispatch);
  registerBuiltinSpells(registry);
  return registry;
}
